// ============================================================================
// raidHistory — client-side ring buffer of resolved raids per workspace.
// ----------------------------------------------------------------------------
// Server-authoritative history (writes to Firestore) is Phase 2 scope; for now
// we cache the last N resolved raids in localStorage so the UI can render a
// Past Raids section and the user can see continuity across sessions on the
// same device. Debug bypass writes here too so the /?noSignIn=debug flow can
// exercise the full lifecycle without a Firestore emulator.
// ============================================================================

import type { ActiveRaidModel } from '../types/schema'

export type RaidOutcome = 'VICTORY' | 'FAILURE'

export interface ResolvedRaidRecord {
  raidId: string
  workspaceId: string
  bossTitle: string
  targetElementId: string
  outcome: RaidOutcome
  isPenaltyRaid: boolean
  initialBossHp: number
  finalBossHp: number
  allocatedCreditPool: number
  resolvedAtIso: string
  enrageDeadlineTimestamp: string
  topContributor: { userDisplayName: string; damageDealt: number } | null
  totalDamage: number
  contributorCount: number
}

const STORAGE_KEY_PREFIX = 'timelining:raidHistory:'
const HISTORY_LIMIT = 10
const CHANGE_EVENT = 'timelining:raidHistory-changed'

const keyFor = (workspaceId: string) => `${STORAGE_KEY_PREFIX}${workspaceId}`

const read = (workspaceId: string): ResolvedRaidRecord[] => {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(keyFor(workspaceId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as ResolvedRaidRecord[]) : []
  } catch {
    return []
  }
}

const write = (workspaceId: string, records: ResolvedRaidRecord[]) => {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(keyFor(workspaceId), JSON.stringify(records.slice(0, HISTORY_LIMIT)))
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { workspaceId } }))
  } catch {
    /* localStorage unavailable — swallow */
  }
}

export const listRaidHistory = (workspaceId: string): ResolvedRaidRecord[] => read(workspaceId)

export const appendRaidHistory = (
  workspaceId: string,
  raid: ActiveRaidModel,
  outcome: RaidOutcome,
): ResolvedRaidRecord => {
  const contributors = Object.values(raid.damageLeaderboard)
  const top = contributors.slice().sort((a, b) => b.damageDealt - a.damageDealt)[0] ?? null
  const totalDamage = contributors.reduce((sum, entry) => sum + entry.damageDealt, 0)
  const record: ResolvedRaidRecord = {
    raidId: raid.raidId,
    workspaceId,
    bossTitle: raid.bossTitle,
    targetElementId: raid.targetElementId,
    outcome,
    isPenaltyRaid: raid.isPenaltyRaid,
    initialBossHp: raid.initialBossHp,
    finalBossHp: raid.currentBossHp,
    allocatedCreditPool: raid.allocatedCreditPool,
    resolvedAtIso: new Date().toISOString(),
    enrageDeadlineTimestamp: raid.enrageDeadlineTimestamp,
    topContributor: top ? { userDisplayName: top.userDisplayName, damageDealt: top.damageDealt } : null,
    totalDamage,
    contributorCount: contributors.length,
  }
  const next = [record, ...read(workspaceId).filter((existing) => existing.raidId !== record.raidId)]
  write(workspaceId, next)
  return record
}

export const subscribeRaidHistory = (
  workspaceId: string,
  onChange: (records: ResolvedRaidRecord[]) => void,
): (() => void) => {
  if (typeof window === 'undefined') return () => {}
  onChange(read(workspaceId))
  const storageHandler = (event: StorageEvent) => {
    if (event.key === keyFor(workspaceId)) onChange(read(workspaceId))
  }
  const customHandler = (event: Event) => {
    const detail = (event as CustomEvent<{ workspaceId: string }>).detail
    if (detail?.workspaceId === workspaceId) onChange(read(workspaceId))
  }
  window.addEventListener('storage', storageHandler)
  window.addEventListener(CHANGE_EVENT, customHandler)
  return () => {
    window.removeEventListener('storage', storageHandler)
    window.removeEventListener(CHANGE_EVENT, customHandler)
  }
}

export const clearRaidHistory = (workspaceId: string) => write(workspaceId, [])
